/**
 * GroupPendingCard
 * Presentational card for a pending group invitation (received or sent).
 * Used by FriendsList when rendering GROUP_PENDING items.
 */

import { useMemo } from 'react'
import { View } from 'react-native'
import { PendingInvitationCard } from '../../ui/PendingInvitationCard'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import { getFriendCardStyles } from '../FriendCard/FriendCard.styles'
import { INVITER_FALLBACK_NAME } from '../../../src/friends/constants/groups.constants'
import type { Group } from '../../../src/friends/types/groups.types'

export interface GroupPendingCardProps {
  group: Group
  onAccept: (group: Group) => void
  onDecline: (groupId: string, group: Group) => void
  onRemove: (groupId: string, group: Group) => void
  isLoading: boolean
  disabled: boolean
}

export function GroupPendingCard({
  group,
  onAccept,
  onDecline,
  onRemove,
  isLoading,
  disabled,
}: GroupPendingCardProps) {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const styles = useMemo(() => getFriendCardStyles(theme, themeMode), [theme, themeMode])
  const isReceived = group.invitationType === 'received'
  const inviterName = group.inviter?.name ?? INVITER_FALLBACK_NAME

  return (
    <View
      style={[styles.friendCard, styles.friendCardPending]}
      testID={`group-pending-card-${group.id}`}
    >
      {isReceived ? (
        <PendingInvitationCard
          variant="received"
          title={inviterName}
          message={`invited you to join ${group.name}`}
          onAccept={() => onAccept(group)}
          onDecline={() => onDecline(group.id, group)}
          isLoading={isLoading}
          disabled={disabled}
          testID={`group-pending-received-${group.id}`}
        />
      ) : (
        <PendingInvitationCard
          variant="sent"
          primaryName={group.name}
          statusText="Pending group invitation"
          onRemove={() => onRemove(group.id, group)}
          testID={`group-pending-sent-${group.id}`}
        />
      )}
    </View>
  )
}
